import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Shield, Loader2, AlertTriangle, CheckCircle2, Smartphone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface TwoFactorSetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

export const TwoFactorSetup = ({ onComplete, onCancel }: TwoFactorSetupProps) => {
  const [step, setStep] = useState<'generate' | 'verify'>('generate');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerateQR = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'GOnX Admin TOTP',
      });

      if (enrollError) {
        console.error('Erro ao gerar 2FA:', enrollError);
        setError('Erro ao gerar código QR. Tente novamente.');
        return;
      }

      if (data) {
        setQrCode(data.totp.qr_code);
        setSecret(data.totp.secret);
        setFactorId(data.id);
        setStep('verify');
      }
    } catch (err) {
      console.error('Erro inesperado:', err);
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6 || !factorId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Criar challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) {
        console.error('Erro ao criar challenge:', challengeError);
        setError('Erro ao verificar código. Tente novamente.');
        return;
      }

      // Verificar código
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verificationCode,
      });

      if (verifyError) {
        console.error('Erro ao verificar:', verifyError);
        setError('Código inválido. Verifique e tente novamente.');
        return;
      }

      // Sucesso!
      onComplete();
    } catch (err) {
      console.error('Erro inesperado:', err);
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMTdjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTE3IDE3YzAtMi4yMDktMS43OTEtNC00LTRzLTQgMS43OTEtNCA0IDEuNzkxIDQgNCA0IDQtMS43OTEgNC00em0wLTE3YzAtMi4yMDktMS43OTEtNC00LTRzLTQgMS43OTEtNCA0IDEuNzkxIDQgNCA0IDQtMS43OTEgNC00em0tMzQgMTdjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMTdjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />

      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="p-4 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30">
              <Shield className="h-12 w-12 text-amber-500" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Configurar Autenticação 2FA</h1>
          <p className="text-slate-400 text-sm">
            Para sua segurança, o acesso administrativo requer autenticação de dois fatores
          </p>
        </div>

        <Card className="shadow-2xl border-slate-800 bg-slate-900/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-lg text-white flex items-center justify-center gap-2">
              <Smartphone className="h-5 w-5 text-amber-500" />
              {step === 'generate' ? 'Iniciar Configuração' : 'Verificar Código'}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {step === 'generate' 
                ? 'Você precisará de um aplicativo autenticador como Google Authenticator ou Authy'
                : 'Escaneie o QR Code e digite o código de 6 dígitos'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <Alert variant="destructive" className="bg-red-950/50 border-red-900">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {step === 'generate' ? (
              <div className="space-y-4">
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Passo 1</p>
                      <p className="text-xs text-slate-400">Instale um app autenticador (Google Authenticator, Authy, etc.)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Passo 2</p>
                      <p className="text-xs text-slate-400">Escaneie o QR Code que será exibido</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Passo 3</p>
                      <p className="text-xs text-slate-400">Digite o código de 6 dígitos para confirmar</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleGenerateQR}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Gerando...
                    </>
                  ) : (
                    'Gerar QR Code'
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* QR Code */}
                {qrCode && (
                  <div className="flex flex-col items-center space-y-4">
                    <div className="bg-white p-4 rounded-xl">
                      <img 
                        src={qrCode} 
                        alt="QR Code para 2FA" 
                        className="w-48 h-48"
                      />
                    </div>
                    {secret && (
                      <div className="text-center">
                        <p className="text-xs text-slate-500 mb-1">Ou digite manualmente:</p>
                        <code className="text-xs bg-slate-800 px-3 py-1 rounded text-amber-400 font-mono">
                          {secret}
                        </code>
                      </div>
                    )}
                  </div>
                )}

                {/* Verification Input */}
                <div className="space-y-3">
                  <label className="text-sm text-slate-300 text-center block">
                    Digite o código de 6 dígitos do seu aplicativo:
                  </label>
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={6}
                      value={verificationCode}
                      onChange={setVerificationCode}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} className="bg-slate-800 border-slate-700 text-white" />
                        <InputOTPSlot index={1} className="bg-slate-800 border-slate-700 text-white" />
                        <InputOTPSlot index={2} className="bg-slate-800 border-slate-700 text-white" />
                        <InputOTPSlot index={3} className="bg-slate-800 border-slate-700 text-white" />
                        <InputOTPSlot index={4} className="bg-slate-800 border-slate-700 text-white" />
                        <InputOTPSlot index={5} className="bg-slate-800 border-slate-700 text-white" />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>

                <Button
                  onClick={handleVerify}
                  disabled={isLoading || verificationCode.length !== 6}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      Ativar 2FA
                    </>
                  )}
                </Button>
              </div>
            )}

            <Button
              variant="ghost"
              onClick={onCancel}
              className="w-full text-slate-400 hover:text-white hover:bg-slate-800"
            >
              Cancelar e Sair
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
